import {
  BUILD_ID,
  KNOWN_INTENTS,
  PUBLIC_SERVER_URL,
  healthUrl,
  intentUrl,
  joinUrl,
  serverIsConfigured,
  type KnownIntent,
} from './config';
import './style.css';

type Screen = 'join' | 'spectate';
type Health = 'unknown' | 'offline' | 'ok' | 'down';

const state = {
  screen: 'join' as Screen,
  agentName: '',
  deviceCode: makeDeviceCode(),
  sessionToken: '',
  lastLog: 'Awaiting join.',
  health: (serverIsConfigured() ? 'unknown' : 'offline') as Health,
  healthDetail: '',
};

function makeDeviceCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const chunk = (n: number) =>
    Array.from({ length: n }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
  return `${chunk(4)}-${chunk(4)}`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function healthLabel(): string {
  if (state.health === 'ok') return 'healthy';
  if (state.health === 'down') return 'down';
  if (state.health === 'offline') return 'offline';
  return 'checking…';
}

function healthChip(): string {
  if (state.health === 'offline') {
    return `<button class="chip off" id="health-chip" type="button" title="PUBLIC_SERVER_URL unset">server offline</button>`;
  }
  if (state.health === 'ok') {
    return `<button class="chip ok" id="health-chip" type="button" title="GET /health ok — click to recheck">server healthy</button>`;
  }
  if (state.health === 'down') {
    return `<button class="chip off" id="health-chip" type="button" title="GET /health failed — click to recheck">server down</button>`;
  }
  return `<button class="chip" id="health-chip" type="button" title="Click to recheck">checking health…</button>`;
}

function buildChip(): string {
  return `<span class="chip build" title="PUBLIC_BUILD_ID">build ${escapeHtml(BUILD_ID)}</span>`;
}

function render(): void {
  const app = document.getElementById('app');
  if (!app) return;
  app.innerHTML = `
    <header class="mast">
      <h1>Sparks Arena <span>public client · 0.9.8 local</span></h1>
      <div class="chips">
        ${buildChip()}
        ${healthChip()}
      </div>
    </header>
    <section class="card">
      <p class="banner">
        Thin public-safe shell. Harmless if leaked.
        Config is only <strong>PUBLIC_SERVER_URL</strong> + build id.
        No keys, DB, JWT, or Pie paths. This is not the Phaser arena.
      </p>
    </section>
    ${state.screen === 'join' ? joinScreen() : spectateScreen()}
    <footer class="foot">
      <span>build ${escapeHtml(BUILD_ID)}</span>
      <span>${serverIsConfigured() ? escapeHtml(PUBLIC_SERVER_URL) : 'PUBLIC_SERVER_URL unset'}</span>
      <span>health ${escapeHtml(healthLabel())}</span>
    </footer>
  `;
  wire();
}

function joinScreen(): string {
  return `
    <section class="card">
      <h2>Join</h2>
      <div class="row">
        <label>
          Agent name
          <input id="agent-name" maxlength="32" placeholder="Spark-A" value="${escapeHtml(state.agentName)}" />
        </label>
        <label>
          Device code (stub)
          <input id="device-code" class="code" readonly value="${escapeHtml(state.deviceCode)}" />
        </label>
      </div>
      <div class="actions" style="margin-top:12px">
        <button class="primary" id="btn-join" type="button">Join</button>
        <button id="btn-refresh-code" type="button">New device code</button>
      </div>
      <p class="muted" style="margin-top:10px">
        Device-code + name are the join envelope only. The server stub issues an ephemeral session token in memory.
        ${serverIsConfigured() ? 'Will POST /join.' : 'PUBLIC_SERVER_URL is unset — join stays local and the HUD will show server offline.'}
      </p>
      <p class="muted" style="margin-top:8px">
        Build <strong>${escapeHtml(BUILD_ID)}</strong>
        · health <strong>${escapeHtml(healthLabel())}</strong>
        ${state.healthDetail ? `· <code>${escapeHtml(state.healthDetail)}</code>` : ''}
      </p>
      <pre class="log" id="join-log">${escapeHtml(state.lastLog)}</pre>
    </section>
  `;
}

function spectateScreen(): string {
  const offline = !serverIsConfigured();
  const joined = state.sessionToken.length > 0;
  return `
    <section class="card">
      <h2>Spectate HUD (placeholder)</h2>
      <div class="hud-grid">
        <div class="stat"><span>Follow</span><span class="val">${escapeHtml(state.agentName || '—')}</span></div>
        <div class="stat"><span>Extract</span><span class="val">— / 10</span></div>
        <div class="stat"><span>Clock</span><span class="val">--:--</span></div>
        <div class="stat"><span>Session</span><span class="val">${joined ? 'ephemeral' : 'none'}</span></div>
        <div class="stat"><span>Build</span><span class="val">${escapeHtml(BUILD_ID)}</span></div>
        <div class="stat"><span>Health</span><span class="val">${escapeHtml(healthLabel())}</span></div>
      </div>
      <p class="muted" style="margin-top:10px">
        Snapshot is a stub. Match sim, extract, rating, and lockouts stay on the future hosted server.
      </p>
    </section>
    <section class="card">
      <h2>Intents</h2>
      <div class="actions">
        ${KNOWN_INTENTS.map((type) => `<button class="intent" data-intent="${type}" type="button">${type}</button>`).join('')}
        <button id="btn-back" type="button">Back to join</button>
      </div>
      <p class="muted" style="margin-top:10px">
        ${offline
          ? 'server offline — PUBLIC_SERVER_URL unset. Buttons will not POST.'
          : joined
            ? `POST JSON to ${escapeHtml(intentUrl())}`
            : 'Join first so the stub can attach a session token.'}
      </p>
      <pre class="log" id="intent-log">${escapeHtml(state.lastLog)}</pre>
    </section>
  `;
}

function wire(): void {
  document.getElementById('health-chip')?.addEventListener('click', () => {
    void refreshHealth();
  });
  document.getElementById('btn-join')?.addEventListener('click', () => {
    void onJoin();
  });
  document.getElementById('btn-refresh-code')?.addEventListener('click', () => {
    state.deviceCode = makeDeviceCode();
    state.lastLog = `New device code ${state.deviceCode}`;
    render();
  });
  document.getElementById('agent-name')?.addEventListener('input', (event) => {
    state.agentName = (event.target as HTMLInputElement).value;
  });
  document.getElementById('btn-back')?.addEventListener('click', () => {
    state.screen = 'join';
    render();
  });
  document.querySelectorAll<HTMLButtonElement>('[data-intent]').forEach((btn) => {
    btn.addEventListener('click', () => {
      void onIntent(btn.dataset.intent as KnownIntent);
    });
  });
}

async function onJoin(): Promise<void> {
  const input = document.getElementById('agent-name') as HTMLInputElement | null;
  state.agentName = (input?.value ?? state.agentName).trim();
  if (!state.agentName) {
    state.lastLog = 'Agent name required.';
    render();
    return;
  }

  if (!serverIsConfigured()) {
    state.sessionToken = '';
    state.screen = 'spectate';
    state.lastLog = 'server offline';
    render();
    return;
  }

  try {
    const res = await fetch(joinUrl(), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        agentName: state.agentName,
        deviceCode: state.deviceCode,
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.sessionToken) {
      state.lastLog = `Join rejected (${res.status}): ${JSON.stringify(body)}`;
      render();
      return;
    }
    state.sessionToken = String(body.sessionToken);
    state.screen = 'spectate';
    state.lastLog = `Joined as ${state.agentName}. Ephemeral session issued.`;
    render();
  } catch (error) {
    state.lastLog = `Join failed: ${error instanceof Error ? error.message : String(error)}`;
    render();
  }
}

async function onIntent(type: KnownIntent): Promise<void> {
  if (!serverIsConfigured()) {
    state.lastLog = 'server offline';
    render();
    return;
  }
  if (!state.sessionToken) {
    state.lastLog = 'Join first — no session token.';
    render();
    return;
  }

  const payload =
    type === 'move'
      ? { x: 840, y: 480 }
      : type === 'mark'
        ? { kind: 'mid' }
        : {};

  try {
    const res = await fetch(intentUrl(), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        sessionToken: state.sessionToken,
        type,
        at: Date.now(),
        payload,
      }),
    });
    const body = await res.json().catch(() => ({}));
    state.lastLog = `${type} → ${res.status}\n${JSON.stringify(body, null, 2)}`;
    render();
  } catch (error) {
    state.lastLog = `Intent failed: ${error instanceof Error ? error.message : String(error)}`;
    render();
  }
}

async function refreshHealth(): Promise<void> {
  if (!serverIsConfigured()) {
    if (state.health !== 'offline') {
      state.health = 'offline';
      state.healthDetail = '';
      render();
    }
    return;
  }

  try {
    const res = await fetch(healthUrl(), { cache: 'no-store' });
    const body = (await res.json().catch(() => ({}))) as { ok?: boolean };
    const next: Health = res.ok && body.ok === true ? 'ok' : 'down';
    const detail = JSON.stringify(body);
    if (state.health !== next || state.healthDetail !== detail) {
      state.health = next;
      state.healthDetail = detail;
      render();
    }
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    if (state.health !== 'down' || state.healthDetail !== detail) {
      state.health = 'down';
      state.healthDetail = detail;
      render();
    }
  }
}

render();
void refreshHealth();
